import { useParams, useNavigate } from "react-router-dom";
import { Shell } from "@/components/layout/Shell";
import { useContracts } from "@/hooks/useContracts";
import { Skeleton } from "@/components/ui/skeleton";
import { format, addDays, addMonths, addYears } from "date-fns";
import { RefreshCw, ArrowLeft } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";
import { ContractForm } from "@/components/contracts/form/ContractForm";
import { ContactPerson } from "@/lib/types";
import { PlanSelectDialog } from "@/components/settings/PlanSelectDialog";
import { Button } from "@/components/ui/button";

export default function ContractEditPage() {
  const { t, tElement } = useEnhancedTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { useContractQuery } = useContracts();
  
  const { 
    data: contract, 
    isLoading, 
    isError 
  } = useContractQuery(id || "");

  const normalizeDate = (date: Date): Date => {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(12, 0, 0, 0);
    return normalizedDate;
  };

  const handleBack = () => {
    window.history.back();
  };

  if (isLoading) {
    return (
      <Shell>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-64" />
          </div>
          <div className="space-y-8">
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </Shell>
    );
  }

  if (isError || !contract) {
    return (
      <Shell>
        <div className="p-6 bg-destructive/10 rounded-md">
          <h1 className="text-xl font-medium text-destructive">{t('errorLoadingContract')}</h1>
          <p className="mt-2">
            {t('contractNotFound')}
          </p>
          <button
            onClick={() => navigate("/contracts")}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md"
          >
            {t('backToContracts')}
          </button>
        </div>
      </Shell>
    );
  }

  const calculateRenewalDate = (date: Date, contractType: any) => {
    if (!contractType?.auto_renews) return null;
    
    const interval = contractType.renewal_interval || 1;
    const unit = contractType.renewal_unit || 'Year';
    
    if (unit === 'Day') {
      return addDays(date, interval);
    } else if (unit === 'Month') {
      return addMonths(date, interval);
    } else if (unit === 'Year') {
      return addYears(date, interval);
    }
    
    return null;
  };
  
  const signedDate = contract.signedDate instanceof Date ? contract.signedDate : new Date(contract.signedDate);
  
  const renewalDate = contract.type?.auto_renews 
    ? calculateRenewalDate(signedDate, contract.type)
    : null;
  
  const hasRenewalInfo = contract.type?.auto_renews && renewalDate;

  const validContactPersons = Array.isArray(contract.contactPersons) 
    ? contract.contactPersons 
    : [];
  
  const sanitizedContract = {
    ...contract,
    signedDate: normalizeDate(contract.signedDate instanceof Date ? contract.signedDate : new Date(contract.signedDate)),
    expiryDate: normalizeDate(contract.expiryDate instanceof Date ? contract.expiryDate : new Date(contract.expiryDate)),
    contactPersons: validContactPersons,
    contactPersonIds: Array.isArray(contract.contactPersons) 
      ? contract.contactPersons.map((contact: ContactPerson) => contact.id)
      : []
  };

  return (
    <Shell>
      <div className="space-y-6 pb-24">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{t('editContract', 'Edit Contract')}</h1>
              <p className="text-muted-foreground mt-2">
                {t('updateContract', 'Update')} {contract.supplierName}
              </p>
            </div>
          </div>
        </div>

        {hasRenewalInfo && (
          <Alert>
            <RefreshCw className="h-4 w-4" />
            <AlertTitle>{t('autoRenews', 'Auto-renews')}</AlertTitle>
            <AlertDescription>
              {t('contractWillAutoRenew', 'This contract will automatically renew every')} {contract.type?.renewal_interval || 1} {t(contract.type?.renewal_unit?.toLowerCase() || 'year')}
              {(contract.type?.renewal_interval || 1) > 1 ? 's' : ''}.
              {renewalDate && ` ${t('nextRenewalOn', 'Next renewal on')} ${format(renewalDate, "MMMM d, yyyy")}.`}
            </AlertDescription>
          </Alert>
        )}

        <ContractForm contract={sanitizedContract} mode="edit" />
      </div>
      
      <PlanSelectDialog />
    </Shell>
  );
}
